import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Pause, Zap, Trash2, ExternalLink, Loader2, AlertCircle, Send, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  product: "Product",
  comparison: "Comparison",
  buying_guide: "Buying guide",
  authenticity: "Authenticity",
  regulatory: "Regulatory",
  news: "News",
  clinical: "Clinical",
  price_guide: "Pricing",
};

const RECOMMENDED = 20;

export const JournalAdmin = () => {
  const [config, setConfig] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rate, setRate] = useState(RECOMMENDED);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tick, setTick] = useState(0);

  // Refresh config + posts every 8s while open
  useEffect(() => {
    const load = async () => {
      try {
        const [c, p, s] = await Promise.all([
          axios.get(`${API}/admin/journal/config`),
          axios.get(`${API}/admin/journal/posts?limit=50`),
          axios.get(`${API}/admin/journal/stats`),
        ]);
        setConfig(c.data);
        setPosts(p.data.posts || []);
        setStats(s.data);
        if (c.data?.posts_per_day && tick === 0) setRate(c.data.posts_per_day);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const id = setInterval(() => setTick((t) => t + 1), 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [tick]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/journal/start`, { posts_per_day: rate });
      setConfig(data);
      toast.success(`Auto-publishing started — ${rate} posts / day`);
    } catch (e) {
      toast.error("Could not start scheduler");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/journal/stop`);
      setConfig(data);
      toast.info("Auto-publishing paused");
    } catch (e) {
      toast.error("Could not stop scheduler");
    } finally {
      setBusy(false);
    }
  };

  const generateOne = async () => {
    setGenerating(true);
    toast.info("Generating one post now…", { description: "May take 20-40s (LLM + image search)" });
    try {
      const { data } = await axios.post(`${API}/admin/journal/generate-now`);
      if (data.success) {
        toast.success("Post generated");
        setTick((t) => t + 1);
      } else {
        toast.error("Generation failed — see backend logs");
      }
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const removePost = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/admin/journal/posts/${id}`);
      setPosts((arr) => arr.filter((p) => p.id !== id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL journal posts and reset the anti-repeat tracker? This cannot be undone.")) return;
    try {
      const { data } = await axios.delete(`${API}/admin/journal/clear`);
      toast.success(`${data.deleted_posts} posts deleted · ${data.cleared_combos} combos reset`);
      setPosts([]);
      setTick((t) => t + 1);
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  const submitAllToIndexNow = async () => {
    setSubmitting(true);
    toast.info("Submitting all URLs to IndexNow…");
    try {
      const { data } = await axios.post(`${API}/admin/indexnow/submit-all`);
      toast.success(`Submitted ${data.total_urls || 0} URLs to IndexNow`, {
        description: `HTTP ${data.status || "—"} · Bing sitemap ping: ${data.sitemap_ping?.bing || "—"}`,
      });
    } catch (e) {
      toast.error("IndexNow submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="py-16 flex items-center justify-center text-neutral-500">
        <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading scheduler…
      </div>
    );
  }

  const rates = config.allowed_rates || [5, 10, 20, 30, 40, 50];
  const running = !!config.running;

  return (
    <section data-testid="admin-journal">
      {/* Status header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-7 border border-neutral-200 p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-3">Scheduler</div>
          <div className="flex items-center gap-3 mb-4">
            <span className={`w-2.5 h-2.5 rounded-full ${running ? "bg-emerald-500 animate-pulse" : "bg-neutral-300"}`} data-testid="journal-status-dot" />
            <span className="font-heading text-3xl font-black tracking-tight">
              {running ? "Auto-publishing" : "Paused"}
            </span>
            {running && (
              <span className="text-sm text-neutral-500">
                @ {config.posts_per_day} posts/day
              </span>
            )}
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <StatItem label="Total posted" value={config.total_posted || 0} />
            <StatItem label="Errors" value={config.errors || 0} />
            <StatItem label="Last run" value={config.last_run_at ? new Date(config.last_run_at).toLocaleString() : "—"} small />
            <StatItem label="Next run" value={config.next_run_at && running ? new Date(config.next_run_at).toLocaleString() : "—"} small />
          </dl>
          {config.last_error && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 inline-flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Last error: {config.last_error}</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="lg:col-span-5 border border-neutral-200 p-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-3">Frequency</div>
            <div className="flex flex-wrap gap-2" data-testid="rate-selector">
              {rates.map((r) => (
                <button
                  key={r}
                  onClick={() => setRate(r)}
                  className={`px-3.5 py-2 text-xs font-bold tracking-wide border transition-colors ${
                    rate === r ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"
                  }`}
                  data-testid={`rate-${r}`}
                >
                  {r}/day
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-neutral-500 font-light">
              Recommended: <span className="font-bold text-black">{RECOMMENDED}/day</span>. Higher rates can trigger search-engine spam signals.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {running ? (
              <button
                onClick={stop}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-black hover:bg-black hover:text-white text-sm font-medium disabled:opacity-50"
                data-testid="journal-stop-btn"
              >
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button
                onClick={start}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-black text-white hover:bg-neutral-800 text-sm font-medium disabled:opacity-50"
                data-testid="journal-start-btn"
              >
                <Play className="w-4 h-4" /> Start auto-publishing
              </button>
            )}
            <button
              onClick={generateOne}
              disabled={generating}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-neutral-300 hover:border-black text-sm font-medium disabled:opacity-50"
              data-testid="journal-generate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Generate 1 now
            </button>
          </div>
          <button
            onClick={clearAll}
            className="text-xs text-red-600 border-b border-red-300 pb-0.5 hover:border-red-600"
            data-testid="journal-clear-btn"
          >
            Clear all posts &amp; reset
          </button>
        </div>
      </div>

      {/* IndexNow row */}
      <div className="border border-neutral-200 p-6 mb-10" data-testid="indexnow-block">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-2">Search engines</div>
            <h3 className="font-heading text-xl font-black tracking-tight mb-1">IndexNow auto-submission.</h3>
            <p className="text-sm text-neutral-600 font-light">
              Every new journal post is auto-pinged to Bing / Yandex / Seznam / Naver. Use the button to bulk-submit the whole site (home + categories + products + journal) immediately.
            </p>
          </div>
          <button
            onClick={submitAllToIndexNow}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-3 border border-black hover:bg-black hover:text-white text-sm font-medium disabled:opacity-50"
            data-testid="indexnow-submit-all-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit entire site
          </button>
        </div>
      </div>

      {/* Publishing log / stats */}
      <PublishingLog stats={stats} />

      {/* Post list */}
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-heading text-2xl font-black tracking-tight">Recent posts ({posts.length})</h2>
      </div>

      {posts.length === 0 ? (
        <p className="text-sm text-neutral-500 py-12 border border-dashed border-neutral-300 text-center">
          No posts yet. Click <span className="font-bold">Start</span> or <span className="font-bold">Generate 1 now</span> to publish.
        </p>
      ) : (
        <div className="border border-neutral-200">
          {posts.map((p) => (
            <div key={p.id} className="flex items-start gap-4 p-4 border-b last:border-b-0 border-neutral-100" data-testid={`journal-row-${p.id}`}>
              <img src={p.image} alt="" className="w-20 h-20 object-cover bg-neutral-100 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                  <span>{TOPIC_LABELS[p.topic_type] || p.topic_type}</span>
                  <span>·</span>
                  <span>{p.product_name}</span>
                </div>
                <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" className="block font-heading text-base font-bold tracking-tight mt-1 hover:underline truncate">
                  {p.title}
                </a>
                <p className="text-xs text-neutral-500 mt-1 truncate">{p.subtitle}</p>
                <p className="text-[11px] text-neutral-400 mt-1">{new Date(p.published_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-col gap-1 items-end shrink-0">
                <a
                  href={`/journal/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-neutral-700 hover:text-black inline-flex items-center gap-1"
                  data-testid={`journal-open-${p.id}`}
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={() => removePost(p.id)}
                  className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid={`journal-delete-${p.id}`}
                >
                  Delete <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const StatItem = ({ label, value, small }) => (
  <div>
    <dt className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</dt>
    <dd className={`font-heading font-black tracking-tight ${small ? "text-xs leading-snug mt-1" : "text-2xl mt-1"}`}>{value}</dd>
  </div>
);

// ─── Publishing Log (counters + 30-day histogram + breakdowns) ───
const PublishingLog = ({ stats }) => {
  if (!stats) {
    return (
      <div className="border border-neutral-200 p-6 mb-10 flex items-center justify-center text-neutral-500" data-testid="publishing-log-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading publishing log…
      </div>
    );
  }

  const daily = stats.daily_last_30 || [];
  const maxDaily = Math.max(1, ...daily.map((d) => d.count));
  const monthly = stats.monthly_last_12 || [];
  const byTopic = stats.by_topic || [];
  const byCategory = stats.by_category || [];

  // Find peak day
  const peakDay = daily.reduce((acc, d) => (d.count > acc.count ? d : acc), { date: "—", count: 0 });

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00Z");
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return iso;
    }
  };
  const fmtMonth = (ym) => {
    try {
      const [y, m] = ym.split("-");
      return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch {
      return ym;
    }
  };

  return (
    <section className="border border-neutral-200 p-6 mb-10" data-testid="publishing-log">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-4 h-4" strokeWidth={2} />
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium">Publishing log</div>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight mb-6">Posts published — by period.</h3>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200 mb-8" data-testid="publishing-log-kpis">
        <Kpi label="Today" value={stats.today} testid="kpi-today" />
        <Kpi label="Last 7 days" value={stats.last_7_days} testid="kpi-week" />
        <Kpi label="Last 30 days" value={stats.last_30_days} testid="kpi-month" />
        <Kpi label="All-time" value={stats.total_all_time} testid="kpi-total" />
      </div>

      {/* Daily histogram — last 30 days */}
      <div className="mb-8" data-testid="publishing-log-daily">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="font-heading text-sm font-black tracking-tight">Last 30 days</h4>
          <p className="text-[11px] text-neutral-500 font-light">
            Peak: <span className="font-bold text-black">{peakDay.count}</span> on {fmtDate(peakDay.date)}
          </p>
        </div>
        <div className="flex items-end gap-1 h-32 border-b border-neutral-200" role="img" aria-label="Daily posts last 30 days">
          {daily.map((d) => {
            const h = d.count === 0 ? 2 : Math.max(6, Math.round((d.count / maxDaily) * 120));
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative" data-testid={`bar-${d.date}`}>
                <div
                  className={`w-full ${d.count > 0 ? "bg-black hover:bg-emerald-600" : "bg-neutral-200"} transition-colors`}
                  style={{ height: `${h}px` }}
                  title={`${fmtDate(d.date)}: ${d.count} post${d.count === 1 ? "" : "s"}`}
                />
                <span className="absolute -top-7 text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white px-1.5 py-0.5 whitespace-nowrap pointer-events-none">
                  {d.count}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-neutral-400 font-light">
          <span>{fmtDate(daily[0]?.date)}</span>
          <span>{fmtDate(daily[Math.floor(daily.length / 2)]?.date)}</span>
          <span>{fmtDate(daily[daily.length - 1]?.date)}</span>
        </div>
      </div>

      {/* Monthly + breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BreakdownList
          title="By month"
          rows={monthly.map((m) => ({ label: fmtMonth(m.month), count: m.count }))}
          testid="breakdown-monthly"
        />
        <BreakdownList
          title="By topic"
          rows={byTopic.map((t) => ({ label: TOPIC_LABELS[t.topic] || t.topic, count: t.count }))}
          testid="breakdown-topic"
        />
        <BreakdownList
          title="By category"
          rows={byCategory.map((c) => ({ label: c.category, count: c.count }))}
          testid="breakdown-category"
        />
      </div>
    </section>
  );
};

const Kpi = ({ label, value, testid }) => (
  <div className="bg-white p-5" data-testid={testid}>
    <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</div>
    <div className="font-heading text-3xl font-black tracking-tight mt-2">{value}</div>
  </div>
);

const BreakdownList = ({ title, rows, testid }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-neutral-200 p-4" data-testid={testid}>
        <h4 className="font-heading text-sm font-black tracking-tight mb-2">{title}</h4>
        <p className="text-xs text-neutral-400 font-light">No data yet.</p>
      </div>
    );
  }
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className="border border-neutral-200 p-4" data-testid={testid}>
      <h4 className="font-heading text-sm font-black tracking-tight mb-3">{title}</h4>
      <ul className="space-y-2">
        {rows.map((r) => {
          const pct = total ? Math.round((r.count / total) * 100) : 0;
          return (
            <li key={r.label} className="text-xs">
              <div className="flex justify-between mb-1">
                <span className="text-neutral-700 truncate">{r.label}</span>
                <span className="font-bold tabular-nums">{r.count}</span>
              </div>
              <div className="w-full h-1 bg-neutral-100">
                <div className="h-1 bg-black" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
